import { makeUseAxios, Options } from "axios-hooks";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { merge } from "lodash";
import LRU from "lru-cache";
import { ApiBaseProps } from "./api-base-props";
import { ApiBaseResponse, EnumRetornoApiBase } from './api-base-response';
import { RetornoApiModel } from "./api-response-model";
import { VariaveisAmbiente } from "ambiente";
import { useObterToken } from "services/store/obter-token";
import { useHistory } from "react-router-dom";


type RequestParams = AxiosRequestConfig & {
  enviarTokenUsuario: boolean;
};

const defaultTimeout = 60 * 1000; /* 10 Segundos */
const useAxios = makeUseAxios({
  axios: axios.create({
    baseURL: `${VariaveisAmbiente.cardapioApi}api`,
    timeout: defaultTimeout,
    timeoutErrorMessage:
      "O tempo da requisição foi excedido. Verifique sua conexão com internet e tente novamente.",
  }),
  cache: new LRU({ max: 10 }),
});


export function useApiBase<T = any>(props?: ApiBaseProps) {

  const { config, opcoes } = props || {};
  const [{ loading: carregandoAxios }, invocarAxios] = useAxios<any>(
    config || "",
    merge({ manual: true }, opcoes)
  );

  const [carregandoInterno, setCarregandoInterno] = useState(false);

  const history = useHistory()
  // const { refreshUser } = useRefreshToken();

  useEffect(() => {
    setCarregandoInterno(carregandoAxios);
  }, [carregandoAxios]);

  const invocarApi = useCallback(async (cfg?: RequestParams, options?: Options, tentarNovamente: boolean = true): Promise<RetornoApiModel<T>> => {
    const objRet = { tipoRetorno: EnumRetornoApiBase.Servidor, statusCode: 0 };
    let resultado: AxiosResponse<any> | undefined;
    let erro: Error | undefined;
    const token = useObterToken.getState().token

    const hostname = window.location.hostname
    const isLocalhost = hostname.includes('localhost')
    const isIPv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)
    const isIPv6 = hostname.includes(':')
    const domainHeader = (isLocalhost || isIPv4 || isIPv6) ? 'menuvip.com.br' : hostname

    resultado = undefined;
    erro = undefined;

    try {
      const tokenEnvio = cfg?.enviarTokenUsuario
        ? token || null
        : null;
      resultado = await invocarAxios(
        {

          ...cfg,
          headers: {
            ...(!cfg?.enviarTokenUsuario ? null : { Authorization: `Bearer ${tokenEnvio}` }),
            'FrontUrl': domainHeader,
            'Domain': domainHeader,
            ...cfg?.headers,
          },
        },
        options
      );
      const { tipoRetorno: tpretorno, statusCode: retStatus } = ApiBaseResponse(undefined, resultado);
      objRet.statusCode = retStatus;
      objRet.tipoRetorno = tpretorno;

    } catch (e: Error | any) {
      if (VariaveisAmbiente.isDev) console.log(e);
      erro = e;

      const { tipoRetorno: tpretorno, statusCode: retStatus } = ApiBaseResponse(e, e.response);
      objRet.statusCode = retStatus;
      objRet.tipoRetorno = tpretorno;

      //TRATAMENTO DE ERRO DE API
      if (e.response && e.response?.data?.title) {
        if (e.response?.data?.errors) {
          if (Object.keys(e.response?.data?.errors).length > 1) {
            let errosArr: string[] = [];

            Object.keys(e.response?.data?.errors)?.forEach((key) => {
              e.response?.data?.errors[key]?.forEach((erro: string) => {
                errosArr.push(`${key}: ${erro}. `);
              });
            });

            erro = new Error(errosArr.join('').trim());
          } else {
            const firstError = Object.entries(e.response?.data?.errors)
            erro = new Error(firstError.length > 0 ? firstError[0].join(': ') : e.response?.data?.title);
          }
        } else {
          erro = new Error(e.response?.data?.title);
        }
      }

      switch (objRet.tipoRetorno) {
        case EnumRetornoApiBase.Local:
          let msg = erro?.message?.toString() || "";
          if (msg.indexOf("timeout") > -1 && msg.indexOf("exceeded") > -1) {
            erro = new Error(
              `O servidor demorou muito para processar a requisição (${(cfg?.timeout || defaultTimeout) / 1000
              }s).`
            );
          }
          else if (msg.toLowerCase().indexOf("network error") > -1) {
            erro = new Error(
              `Não foi possível conectar ao servidor. Verifique sua conexão com internet e tente novamente.` +
              (msg.length > 0 ? `Detalhe: ${msg}` : ``)
            );
          }
          break;
        case EnumRetornoApiBase.Api:
          if (retStatus === 401) {
            if (cfg?.enviarTokenUsuario && tentarNovamente) {
              const tokenAtual = useObterToken.getState().token;
              try {
                const resp = await axios.post(
                  `${VariaveisAmbiente.cardapioApi}api/v4/Auth/refresh-token`,
                  { token: tokenAtual ?? '' },
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      'Domain': domainHeader,
                    },
                    timeout: defaultTimeout,
                  }
                );
                const novoToken = resp?.data?.accessToken ?? '';
                if (novoToken) {
                  useObterToken.getState().setToken(novoToken);
                  return await invocarApi(cfg, options, false);
                } else {
                  useObterToken.getState().setToken(undefined);
                }
              } catch {
                useObterToken.getState().setToken(undefined);
              }
            }
          }
          if (retStatus === 402) {
            resultado = e.response
          }
          break;
        case EnumRetornoApiBase.Servidor:
          erro = new Error(
            `O procedimento solicitado causou um erro no servidor. Tente novamente em alguns instantes. Detalhe: ${erro?.message}`
          );
          break;
      }

      resultado = e.response
    }


    setCarregandoInterno(false);

    return {
      resultado,
      erro,
      tipoRetorno: objRet.tipoRetorno,
      statusCode: objRet.statusCode,
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invocarAxios, history]
  );

  return {
    invocarApi,
    carregando: carregandoInterno,
  };
}
